import { useEffect, useState, useCallback } from "react";
import api, { errorMessage } from "../lib/api";
import { PageHeader, Stat, ActionButton, GhostButton, FormField, inputClass } from "../components/Primitives";
import { Modal } from "../components/Modal";
import { Download, FileText, CalendarRange, Settings2, ChevronLeft, ChevronRight, RotateCcw, Lock, LockOpen } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const FREQ_LABEL = { weekly: "Weekly", biweekly: "Biweekly", semimonthly: "Semimonthly", monthly: "Monthly" };
const FREQ_OPTIONS = [
  ["weekly", "Weekly (7 days)"],
  ["biweekly", "Biweekly (14 days)"],
  ["semimonthly", "Semimonthly (1st–15th · 16th–EOM)"],
  ["monthly", "Monthly (1st–EOM)"],
];

// Shift the active payroll period forward (`dir=+1`) or backward (`dir=-1`)
// based on frequency. Returns `{start, end}` as YYYY-MM-DD strings.
function shiftPeriod(startStr, endStr, frequency, dir) {
  const start = new Date(`${startStr}T00:00:00Z`);
  const end = new Date(`${endStr}T00:00:00Z`);
  const fmt = (d) => d.toISOString().slice(0, 10);
  const addDays = (d, n) => { const x = new Date(d); x.setUTCDate(x.getUTCDate() + n); return x; };
  if (frequency === "weekly")   return { start: fmt(addDays(start, 7 * dir)),  end: fmt(addDays(end, 7 * dir)) };
  if (frequency === "biweekly") return { start: fmt(addDays(start, 14 * dir)), end: fmt(addDays(end, 14 * dir)) };
  if (frequency === "semimonthly") {
    const y = start.getUTCFullYear(), m = start.getUTCMonth(), d = start.getUTCDate();
    if (dir < 0) {
      if (d === 1) { // → prev month's 16th–EOM
        const pm = new Date(Date.UTC(y, m, 0)); // last day of prev month
        return { start: fmt(new Date(Date.UTC(pm.getUTCFullYear(), pm.getUTCMonth(), 16))), end: fmt(pm) };
      }
      return { start: fmt(new Date(Date.UTC(y, m, 1))), end: fmt(new Date(Date.UTC(y, m, 15))) };
    }
    // dir > 0
    if (d === 1) {
      return { start: fmt(new Date(Date.UTC(y, m, 16))), end: fmt(new Date(Date.UTC(y, m + 1, 0))) };
    }
    return { start: fmt(new Date(Date.UTC(y, m + 1, 1))), end: fmt(new Date(Date.UTC(y, m + 1, 15))) };
  }
  // monthly
  const y = start.getUTCFullYear(), m = start.getUTCMonth();
  const ns = new Date(Date.UTC(y, m + dir, 1));
  const ne = new Date(Date.UTC(ns.getUTCFullYear(), ns.getUTCMonth() + 1, 0));
  return { start: fmt(ns), end: fmt(ne) };
}

export default function Payroll() {
  const { user } = useAuth() || {};
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [data, setData] = useState({ rows: [], period_label: "", period_start: "", period_end: "", frequency: "biweekly" });
  const [override, setOverride] = useState({ start: "", end: "" });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState({ frequency: "biweekly", week_starts: "monday", anchor_date: "", timezone: "America/New_York" });
  const [savingSettings, setSavingSettings] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditData, setAuditData] = useState({ lock: null, entries: [] });
  const [auditLoading, setAuditLoading] = useState(false);

  const [loadError, setLoadError] = useState(null);
  const [deductUnpaidBreaks, setDeductUnpaidBreaks] = useState(true);
  const load = useCallback(async () => {
    try {
      const params = { deduct_unpaid_breaks: deductUnpaidBreaks ? "true" : "false" };
      if (override.start && override.end) { params.start = override.start; params.end = override.end; }
      const r = await api.get("/payroll/summary", { params });
      setData(r.data);
      setLoadError(null);
    } catch (err) {
      // Surfaced via the empty-state below; also log for support triage.
      if (process.env.NODE_ENV === "development") console.error("Payroll load failed:", err);
      setLoadError(err?.response?.data?.detail || err?.message || "Failed to load payroll");
    }
  }, [override, deductUnpaidBreaks]);
  useEffect(() => { load(); }, [load]);

  const qs = (() => {
    const parts = [];
    if (override.start && override.end) { parts.push(`start=${override.start}`, `end=${override.end}`); }
    parts.push(`deduct_unpaid_breaks=${deductUnpaidBreaks ? "true" : "false"}`);
    return parts.length ? `?${parts.join("&")}` : "";
  })();
  const exportUrl = (ext) => `/payroll/export.${ext}${qs}`;
  const downloadBlob = (blob, ext) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payroll_${data.period_start || "all"}_${data.period_end || ""}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const exportCsv = async () => {
    const res = await api.get(exportUrl("csv"), { responseType: "blob" });
    downloadBlob(res.data, "csv");
  };
  const exportPdf = async () => {
    const res = await api.get(exportUrl("pdf"), { responseType: "blob" });
    downloadBlob(res.data, "pdf");
  };

  const openSettings = async () => {
    try {
      const r = await api.get("/agencies/me");
      const ps = r.data?.payroll_settings || {};
      setSettings({
        frequency: ps.frequency || "biweekly",
        week_starts: ps.week_starts || "monday",
        anchor_date: ps.anchor_date || "",
        timezone: ps.timezone || "America/New_York",
        auto_approve_adhoc: !!ps.auto_approve_adhoc,
        auto_approve_adhoc_max_hours: ps.auto_approve_adhoc_max_hours || 12,
      });
      setSettingsOpen(true);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load settings"));
    }
  };
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.patch("/agencies/payroll-settings", {
        frequency: settings.frequency,
        week_starts: settings.week_starts,
        anchor_date: settings.anchor_date || null,
        timezone: settings.timezone || null,
        auto_approve_adhoc: !!settings.auto_approve_adhoc,
        auto_approve_adhoc_max_hours: Number(settings.auto_approve_adhoc_max_hours) || 12,
      });
      toast.success("Payroll settings saved");
      setSettingsOpen(false);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Save failed"));
    } finally {
      setSavingSettings(false);
    }
  };

  const rows = data.rows || [];
  const totals = rows.reduce((a, r) => {
    a.scheduled += r.scheduled_hours || 0;
    a.worked += r.total_hours || 0;
    a.adj += r.adj_hours || 0;
    a.overtime += r.overtime_hours || 0;
    a.earnings += r.earnings || 0;
    a.shifts += r.shifts_count || 0;
    return a;
  }, { scheduled: 0, worked: 0, adj: 0, overtime: 0, earnings: 0, shifts: 0 });
  const totalVariance = totals.worked - totals.scheduled;

  const stepPeriod = (dir) => {
    if (!data.period_start || !data.period_end) return;
    const next = shiftPeriod(data.period_start, data.period_end, data.frequency, dir);
    setOverride({ start: next.start, end: next.end });
  };
  const jumpToday = () => setOverride({ start: "", end: "" });

  const toggleLock = async () => {
    if (data.locked && data.lock) {
      // Open the drill-down modal (compliance check + unlock in one place).
      setAuditOpen(true);
      setAuditLoading(true);
      try {
        const res = await api.get(`/payroll/lock/${data.lock.id}/audit`);
        setAuditData(res.data);
      } catch (e) {
        toast.error(errorMessage(e, "Failed to load post-lock audit"));
        setAuditOpen(false);
      } finally {
        setAuditLoading(false);
      }
    } else {
      if (!window.confirm(`Lock the pay period ${data.period_start} → ${data.period_end}? Edits to attendance in this window will still go through but will be flagged in the audit log.`)) return;
      try {
        await api.post("/payroll/lock", { start: data.period_start, end: data.period_end, frequency: data.frequency });
        toast.success("Pay period locked");
        load();
      } catch (e) { toast.error(errorMessage(e, "Lock failed")); }
    }
  };

  const unlockFromModal = async () => {
    if (!auditData.lock) return;
    if (!window.confirm(`Unlock the pay period ${auditData.lock.period_start} → ${auditData.lock.period_end}?`)) return;
    try {
      await api.delete(`/payroll/lock/${auditData.lock.id}`);
      toast.success("Pay period unlocked");
      setAuditOpen(false);
      load();
    } catch (e) { toast.error(errorMessage(e, "Unlock failed")); }
  };

  return (
    <div>
      <PageHeader
        title="Payroll"
        subtitle="Per-pay-period actuals computed from real attendance — worked hours, scheduled hours, and variance. Frequency configured under Agency settings."
        action={
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                data-testid="payroll-settings-button"
                onClick={openSettings}
                className="inline-flex items-center gap-2 border border-[#27272A] hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-300 px-4 py-2 text-xs uppercase tracking-widest transition-colors duration-150"
              >
                <Settings2 className="size-4" /> Settings
              </button>
            )}
            <button
              data-testid="export-csv-button"
              onClick={exportCsv}
              className="inline-flex items-center gap-2 border border-[#27272A] hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-300 px-4 py-2 text-xs uppercase tracking-widest transition-colors duration-150"
            >
              <Download className="size-4" /> Export CSV
            </button>
            <button
              data-testid="export-pdf-button"
              onClick={exportPdf}
              className="inline-flex items-center gap-2 border border-[#27272A] hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-300 px-4 py-2 text-xs uppercase tracking-widest transition-colors duration-150"
            >
              <FileText className="size-4" /> Export PDF
            </button>
          </div>
        }
      />

      <div className="p-8 space-y-6">
        <div className="border border-[#27272A] bg-[#121212] px-5 py-4 flex flex-wrap items-center gap-x-8 gap-y-3" data-testid="payroll-period-strip">
          <div>
            <div className="label-mute mb-0.5">Active period</div>
            <div className="flex items-center gap-2">
              <button
                data-testid="payroll-period-prev"
                onClick={() => stepPeriod(-1)}
                title="Previous pay period"
                className="size-7 inline-flex items-center justify-center border border-[#27272A] hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-400 transition-colors"
              >
                <ChevronLeft className="size-3.5" />
              </button>
              <div className="font-display text-lg uppercase font-bold tracking-tight inline-flex items-center gap-2" data-testid="payroll-period-label">
                <CalendarRange className="size-4 text-[#0A84FF]" /> {data.period_label || "—"}
              </div>
              <button
                data-testid="payroll-period-next"
                onClick={() => stepPeriod(+1)}
                title="Next pay period"
                className="size-7 inline-flex items-center justify-center border border-[#27272A] hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-400 transition-colors"
              >
                <ChevronRight className="size-3.5" />
              </button>
              {(override.start || override.end) && (
                <button
                  data-testid="payroll-period-today"
                  onClick={jumpToday}
                  title="Jump back to the current pay period"
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white border border-[#27272A] hover:border-zinc-500 px-2 py-1"
                >
                  <RotateCcw className="size-3" /> Today
                </button>
              )}
              {isAdmin && data.period_start && (
                <button
                  data-testid="payroll-period-lock"
                  onClick={toggleLock}
                  title={data.locked
                    ? `Locked ${data.lock?.locked_at ? new Date(data.lock.locked_at).toLocaleString() : ""} by ${data.lock?.locked_by_name || "?"} — click to unlock`
                    : "Finalise this pay period (later edits get flagged in audit log)"}
                  className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors ${
                    data.locked
                      ? "bg-[#FF3B30] border-[#FF3B30] text-white hover:opacity-90"
                      : "border-[#27272A] hover:border-[#FF3B30] text-zinc-400 hover:text-[#FF3B30]"
                  }`}
                >
                  {data.locked ? <Lock className="size-3" /> : <LockOpen className="size-3" />}
                  {data.locked ? "Locked" : "Lock period"}
                </button>
              )}
            </div>
          </div>
          <div>
            <div className="label-mute mb-0.5">Frequency</div>
            <div className="font-mono text-sm text-zinc-300 uppercase tracking-widest" data-testid="payroll-frequency">{FREQ_LABEL[data.frequency] || data.frequency}</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className="label-mute">Override</div>
            <input
              type="date"
              data-testid="payroll-override-start"
              value={override.start}
              onChange={(e) => setOverride((o) => ({ ...o, start: e.target.value }))}
              className="bg-[#0A0A0A] border border-[#27272A] text-xs font-mono text-zinc-300 px-2 py-1.5 focus:outline-none focus:border-[#FF3B30]"
            />
            <span className="text-zinc-600 text-xs">→</span>
            <input
              type="date"
              data-testid="payroll-override-end"
              value={override.end}
              onChange={(e) => setOverride((o) => ({ ...o, end: e.target.value }))}
              className="bg-[#0A0A0A] border border-[#27272A] text-xs font-mono text-zinc-300 px-2 py-1.5 focus:outline-none focus:border-[#FF3B30]"
            />
            {(override.start || override.end) && (
              <button
                data-testid="payroll-override-clear"
                onClick={() => setOverride({ start: "", end: "" })}
                className="text-[10px] uppercase tracking-widest text-[#FF3B30] hover:text-[#FF453A] px-2 py-1"
              >
                Clear
              </button>
            )}
            <label className="ml-3 inline-flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="payroll-deduct-unpaid-breaks"
                className="accent-[#FF3B30]"
                checked={deductUnpaidBreaks}
                onChange={(e) => setDeductUnpaidBreaks(e.target.checked)}
              />
              <span className="text-[10px] uppercase tracking-widest text-zinc-400" title="Deduct each site's default unpaid-break minutes from Worked & Adj hours (only when shift meets the site's threshold)">
                Deduct unpaid breaks
              </span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Stat label="Total Officers" value={rows.length} />
          <Stat label="Total Shifts" value={totals.shifts} />
          <Stat label="Scheduled Hrs" value={totals.scheduled.toFixed(1)} accent="#0A84FF" />
          <Stat label="Worked Hrs" value={totals.worked.toFixed(1)} accent={totalVariance >= 0 ? "#34C759" : "#FF9F0A"} />
          <Stat label="Overtime Hrs" value={totals.overtime.toFixed(1)} accent={totals.overtime > 0 ? "#FF9F0A" : "#666"} />
          <Stat label="Projected Payout" value={`$${totals.earnings.toLocaleString()}`} accent="#34C759" />
        </div>

        <div className="border border-[#27272A] bg-[#121212]">
          <div className="px-5 py-4 border-b border-[#27272A]">
            <div className="label-mute">Per- breakdown · attendance-driven</div>
            <div className="font-display text-xl uppercase font-bold tracking-tight">Earnings Ledger</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payroll-table">
              <thead className="border-b border-[#27272A] text-left">
                <tr className="label-mute">
                  <th className="px-4 py-3">Badge</th>
                  <th className="px-4 py-3">Officer</th>
                  <th className="px-4 py-3 text-right">Shifts</th>
                  <th className="px-4 py-3 text-right">Scheduled</th>
                  <th className="px-4 py-3 text-right">Worked</th>
                  <th className="px-4 py-3 text-right">Adj Hrs</th>
                  <th className="px-4 py-3 text-right">OT Hrs</th>
                  <th className="px-4 py-3 text-right">Δ Hrs</th>
                  <th className="px-4 py-3 text-right">Edits</th>
                  <th className="px-4 py-3 text-right">Auto-closed</th>
                  <th className="px-4 py-3 text-right">No-show</th>
                  <th className="px-4 py-3 text-right">Rate</th>
                  <th className="px-4 py-3 text-right">Earnings</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#27272A]">
                {rows.map((r) => {
                  const v = r.variance_hours || 0;
                  const vColor = v > 0 ? "text-[#34C759]" : v < 0 ? "text-[#FF9F0A]" : "text-zinc-400";
                  return (
                    <tr key={r.guard_id} className="hover:bg-[#1c1c1e]" data-testid={`payroll-row-${r.guard_id}`}>
                      <td className="px-4 py-3 font-mono text-[#FF9F0A]">{r.badge_no || "—"}</td>
                      <td className="px-4 py-3 font-medium">{r.guard_name}</td>
                      <td className="px-4 py-3 font-mono text-right">{r.shifts_count}</td>
                      <td className="px-4 py-3 font-mono text-right text-zinc-400">{r.scheduled_hours.toFixed(1)}</td>
                      <td className="px-4 py-3 font-mono text-right">{r.total_hours.toFixed(1)}</td>
                      <td className="px-4 py-3 font-mono text-right text-zinc-300" data-testid={`payroll-adj-${r.guard_id}`}>{(r.adj_hours || 0).toFixed(1)}</td>
                      <td className={`px-4 py-3 font-mono text-right ${(r.overtime_hours || 0) > 0 ? "text-[#FF9F0A] font-bold" : "text-zinc-500"}`} data-testid={`payroll-ot-${r.guard_id}`}>{(r.overtime_hours || 0).toFixed(1)}</td>
                      <td className={`px-4 py-3 font-mono text-right ${vColor}`}>{v > 0 ? "+" : ""}{v.toFixed(1)}</td>
                      <td className="px-4 py-3 font-mono text-right text-zinc-500">{r.edited_count || 0}</td>
                      <td className="px-4 py-3 font-mono text-right text-zinc-500">{r.auto_closed_count || 0}</td>
                      <td className="px-4 py-3 font-mono text-right text-zinc-500">{r.no_show_count || 0}</td>
                      <td className="px-4 py-3 font-mono text-right">${r.hourly_rate}</td>
                      <td className="px-4 py-3 font-mono text-right font-bold text-[#34C759]">${r.earnings.toLocaleString()}</td>
                    </tr>
                  );
                })}
                {rows.length === 0 && <tr><td colSpan={13} className="text-center py-16 text-zinc-500 scanlines">No attendance recorded inside the active pay period.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {auditOpen && (
        <Modal title="Post-lock edits" onClose={() => setAuditOpen(false)} testId="payroll-audit-modal">
          {auditData.lock && (
            <div className="border border-[#27272A] bg-[#0A0A0A] px-4 py-3 mb-4 flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="label-mute mb-0.5">Locked period</div>
                <div className="font-display uppercase font-bold tracking-tight">
                  {auditData.lock.period_start} → {auditData.lock.period_end}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                  Locked {new Date(auditData.lock.locked_at).toLocaleString()} by {auditData.lock.locked_by_name || "?"}
                </div>
              </div>
              <button
                data-testid="payroll-audit-unlock"
                onClick={unlockFromModal}
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white px-3 py-1.5 transition-colors"
              >
                <LockOpen className="size-3" /> Unlock period
              </button>
            </div>
          )}
          {auditLoading ? (
            <div className="text-center py-10 text-zinc-500 scanlines">Loading audit trail…</div>
          ) : auditData.entries.length === 0 ? (
            <div className="border border-[#27272A] p-8 text-center text-zinc-400 scanlines" data-testid="payroll-audit-empty">
              No post-lock edits · this period is clean for payroll export.
            </div>
          ) : (
            <div className="border border-[#27272A] overflow-x-auto">
              <table className="w-full text-sm" data-testid="payroll-audit-table">
                <thead className="border-b border-[#27272A] bg-[#0A0A0A]">
                  <tr className="label-mute">
                    <th className="px-3 py-2 text-left">When</th>
                    <th className="px-3 py-2 text-left">By</th>
                    <th className="px-3 py-2 text-left">Session</th>
                    <th className="px-3 py-2 text-left">Before → After</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#27272A]">
                  {auditData.entries.map((e) => (
                    <tr key={e.id} className="hover:bg-[#1c1c1e]" data-testid={`payroll-audit-row-${e.id}`}>
                      <td className="px-3 py-2 font-mono text-xs text-zinc-400 whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                      <td className="px-3 py-2 text-zinc-300 whitespace-nowrap">{e.user_email || e.actor || "?"}</td>
                      <td className="px-3 py-2 font-mono text-xs text-zinc-500">{e.entity_id}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-zinc-400">
                        <div>in: {(e.before?.check_in_time || "—")} → <span className="text-white">{(e.after?.check_in_time || "—")}</span></div>
                        <div>out: {(e.before?.check_out_time || "—")} → <span className="text-white">{(e.after?.check_out_time || "—")}</span></div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-4 flex justify-end">
            <GhostButton onClick={() => setAuditOpen(false)}>Close</GhostButton>
          </div>
        </Modal>
      )}

      {settingsOpen && (
        <Modal title="Payroll Settings" onClose={() => setSettingsOpen(false)} testId="payroll-settings-modal">
          <div className="space-y-4">
            <FormField label="Frequency">
              <select
                data-testid="payroll-settings-frequency"
                className={inputClass}
                value={settings.frequency}
                onChange={(e) => setSettings((s) => ({ ...s, frequency: e.target.value }))}
              >
                {FREQ_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </FormField>
            <FormField label="Week starts on">
              <select
                data-testid="payroll-settings-week-starts"
                className={inputClass}
                value={settings.week_starts}
                onChange={(e) => setSettings((s) => ({ ...s, week_starts: e.target.value }))}
                disabled={settings.frequency !== "weekly" && settings.frequency !== "biweekly"}
              >
                <option value="monday">Monday</option>
                <option value="sunday">Sunday</option>
              </select>
            </FormField>
            <FormField label="Anchor date (biweekly cycle origin · optional)">
              <input
                type="date"
                data-testid="payroll-settings-anchor"
                className={inputClass}
                value={settings.anchor_date || ""}
                onChange={(e) => setSettings((s) => ({ ...s, anchor_date: e.target.value }))}
                disabled={settings.frequency !== "biweekly"}
              />
            </FormField>
            <FormField label="Timezone (IANA)">
              <input
                data-testid="payroll-settings-timezone"
                className={inputClass}
                value={settings.timezone || ""}
                onChange={(e) => setSettings((s) => ({ ...s, timezone: e.target.value }))}
                placeholder="America/New_York"
              />
            </FormField>
            <div className="mt-2 border-t border-[#27272A] pt-3">
              <div className="label-mute mb-2">Ad-hoc shift auto-approval</div>
              <label className="flex items-start gap-2 cursor-pointer select-none">
                <input
                  data-testid="payroll-settings-auto-approve-adhoc"
                  type="checkbox"
                  className="mt-0.5 accent-[#FF3B30]"
                  checked={!!settings.auto_approve_adhoc}
                  onChange={(e) => setSettings((s) => ({ ...s, auto_approve_adhoc: e.target.checked }))}
                />
                <span className="text-sm text-zinc-300">
                  Auto-approve ad-hoc shifts for familiar 
                  <span className="block text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                    Skips the manual audit queue when a  is inside the site geofence, has ≥1 prior shift there, and the shift is ≤ the cap below. Per-site overrides available on each Site.
                  </span>
                </span>
              </label>
              <div className="mt-3">
                <FormField label="Max shift duration for auto-approve (hours)">
                  <input
                    type="number"
                    min="1"
                    max="16"
                    data-testid="payroll-settings-auto-approve-max-hours"
                    className={inputClass}
                    value={settings.auto_approve_adhoc_max_hours || 12}
                    onChange={(e) => setSettings((s) => ({ ...s, auto_approve_adhoc_max_hours: e.target.value }))}
                    disabled={!settings.auto_approve_adhoc}
                  />
                </FormField>
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <GhostButton onClick={() => setSettingsOpen(false)}>Cancel</GhostButton>
            <ActionButton
              data-testid="payroll-settings-save"
              onClick={saveSettings}
              disabled={savingSettings}
            >
              {savingSettings ? "Saving…" : "Save"}
            </ActionButton>
          </div>
        </Modal>
      )}
    </div>
  );
}
